import { createAuthClient } from 'better-auth/react';
import { twoFactorClient } from 'better-auth/client/plugins';

/**
 * EmployeeProfile-owned local authentication — password + TOTP, via
 * Better Auth's own client SDK, replacing Microsoft Entra ID
 * (`docs/AUTHENTICATION_REASSESSMENT.md`). This is the ONLY place this
 * application acquires a session; `api/src/auth/betterAuthAuthAdapter.ts`
 * verifies whatever session cookie the browser presents with no idea how
 * it was obtained.
 *
 * `baseURL: window.location.origin + '/api/auth'` — same-origin, matching
 * the exact convention `client.ts`'s own `VITE_API_BASE_URL` default
 * (`/api`) already establishes: the reverse proxy (NPM in staging,
 * Vite's dev proxy locally) strips `/api/` before forwarding to the API
 * process, where `api/src/app.ts` mounts Better Auth at `/auth/*`.
 * Same-origin throughout is also why the session cookie Better Auth sets
 * is simply sent on every subsequent same-origin request by the browser
 * itself — this module never reads, stores, or attaches it to anything.
 * Better Auth's client requires an absolute URL (it throws at
 * construction time otherwise — confirmed directly), so the origin is
 * always included explicitly; it is never a different origin than the
 * page itself, so this is not a cross-origin request in any real sense.
 *
 * Unlike the removed Entra flow, local auth needs no build-time
 * configuration at all (no client ID, no tenant ID) — `resolveIdentityMode`
 * (`../api/client.ts`) reflects that: 'local' is simply the mode that
 * applies whenever dev mode is not configured, not a mode gated behind
 * its own separate configuration check.
 */
export const authClient = createAuthClient({
  baseURL: `${window.location.origin}/api/auth`,
  plugins: [twoFactorClient()],
});

export const useSession = authClient.useSession;

export type SignInOutcome = { kind: 'signed-in' } | { kind: 'totp-required' } | { kind: 'error'; message: string };

/** A generic, non-account-revealing message for every credential
 * failure — deliberately identical whether the email does not exist or
 * the password is wrong, so a failed sign-in never discloses which one
 * (`docs/AUTHENTICATION.md`'s own account-enumeration note). */
const GENERIC_SIGN_IN_FAILURE = 'Incorrect email or password.';

export async function signInWithPassword(email: string, password: string): Promise<SignInOutcome> {
  const { data, error } = await authClient.signIn.email({ email, password });
  if (error) return { kind: 'error', message: GENERIC_SIGN_IN_FAILURE };
  // Better Auth's own documented shape for "password verified, TOTP still
  // required": the response carries `twoFactorRedirect: true` rather than
  // a signed-in session.
  if ((data as { twoFactorRedirect?: boolean } | null)?.twoFactorRedirect) {
    return { kind: 'totp-required' };
  }
  return { kind: 'signed-in' };
}

export async function verifyTotp(code: string): Promise<SignInOutcome> {
  const { error } = await authClient.twoFactor.verifyTotp({ code });
  if (error) return { kind: 'error', message: 'That code was not accepted. Check your authenticator app and try again.' };
  return { kind: 'signed-in' };
}

export async function signOut(): Promise<void> {
  await authClient.signOut();
}
